import { useI18n, type MessageKey } from '@ecom/i18n';
import { type ReactElement } from 'react';
import { Link } from 'react-router-dom';

import { SkIcon, type SkIconName } from '@/components/ui/SkIcon';
import { cn } from '@/lib/cn';

interface QuickAction {
  readonly titleKey: MessageKey;
  readonly descriptionKey: MessageKey;
  readonly href: string;
  readonly icon: SkIconName;
  readonly primary?: boolean;
}

/**
 * Every shortcut leads to a real screen this app already serves — the six of the Figma design (« Souk 2.0 »), with
 * the most frequent one (a new product) set apart in ABOUEB blue. « + Commande » stays out: the supervisor refused it
 * (« cette fonctionnalité n'existe pas »), « Voir les commandes » stands in its place.
 */
const ACTIONS: readonly QuickAction[] = [
  {
    titleKey: 'merchant.dashboard.quickActions.newProduct',
    descriptionKey: 'merchant.dashboard.quick.newProductDescription',
    href: '/products/new',
    icon: 'plus',
    primary: true,
  },
  {
    titleKey: 'merchant.dashboard.quickActions.viewOrders',
    descriptionKey: 'merchant.dashboard.quick.viewOrdersDescription',
    href: '/orders',
    icon: 'bag',
  },
  {
    titleKey: 'merchant.dashboard.quick.inventory',
    descriptionKey: 'merchant.dashboard.quick.inventoryDescription',
    href: '/inventory',
    icon: 'inventory',
  },
  {
    titleKey: 'merchant.dashboard.quickActions.newCoupon',
    descriptionKey: 'merchant.dashboard.quick.newCouponDescription',
    href: '/promotions?new=1',
    icon: 'coupon',
  },
  {
    titleKey: 'merchant.dashboard.quick.customers',
    descriptionKey: 'merchant.dashboard.quick.customersDescription',
    href: '/customers',
    icon: 'users',
  },
  {
    titleKey: 'merchant.dashboard.quick.store',
    descriptionKey: 'merchant.dashboard.quick.storeDescription',
    href: '/store',
    icon: 'settings',
  },
];

/** « Que souhaitez-vous faire ? » — the Figma design's action cards: icon tile, title, one line, an arrow. */
export function QuickActions(): ReactElement {
  const { t } = useI18n();
  return (
    <section className="sk-quick" aria-labelledby="quick-actions-title">
      <div className="sk-section-head px-0.5">
        <div>
          <span className="sk-eyebrow">{t('merchant.dashboard.quickEyebrow')}</span>
          <h2 id="quick-actions-title">{t('merchant.dashboard.quick.title')}</h2>
        </div>
      </div>
      <div className="sk-quick-grid">
        {ACTIONS.map((action) => (
          <Link
            key={action.href}
            to={action.href}
            className={cn('sk-quick-card', action.primary === true && 'is-primary')}
          >
            <span className="sk-quick-icon">
              <SkIcon name={action.icon} />
            </span>
            <span className="sk-quick-copy">
              <strong>{t(action.titleKey)}</strong>
              <small>{t(action.descriptionKey)}</small>
            </span>
            <SkIcon name="arrow" size={16} />
          </Link>
        ))}
      </div>
    </section>
  );
}
